import { useState } from 'react';
import { agendaSchema, type BotScope } from '@cryptology-assist/domain';
import { botPath } from './api';
import { useRemote } from './hooks';
import { ErrorNotice } from './components';
import { dateLabel, type Copy, type Language } from './copy';
export function Agenda({
  scope,
  copy,
  language,
}: {
  scope: BotScope;
  copy: Copy;
  language: Language;
}) {
  const [before, setBefore] = useState<string | null>(null);
  const agenda = useRemote(
    botPath(scope, `agenda${before ? `?before=${before}` : ''}`),
    agendaSchema
  );
  return (
    <section className="settings-content">
      <div className="section-heading">
        <h1>{copy.agenda}</h1>
        <p>{copy.agendaHelp}</p>
        <button disabled={agenda.loading} onClick={agenda.reload}>
          {copy.refresh}
        </button>
      </div>
      <ErrorNotice error={agenda.error} copy={copy} onRetry={agenda.reload} />
      {agenda.loading && !agenda.data && <p>{copy.loading}</p>}
      {agenda.data?.appointments.length === 0 && <p>{copy.agendaEmpty}</p>}
      <div className="agenda-list">
        {agenda.data?.appointments.map((appointment) => (
          <article className="agenda-appointment" key={appointment.actionId}>
            <div className="section-toolbar">
              <h2>{appointment.subject}</h2>
              {appointment.booking && (
                <span className="badge">
                  {appointment.booking.status === 'confirmed'
                    ? copy.confirmed
                    : copy.cancelled}
                </span>
              )}
            </div>
            {appointment.adviserName && (
              <p>
                {copy.adviserName}: <strong>{appointment.adviserName}</strong>
              </p>
            )}
            {appointment.error ? (
              <div className="notice error" role="alert">
                {copy.agendaCalendarUnavailable}
              </div>
            ) : appointment.booking ? (
              <p className="appointment-time">
                {dateLabel(appointment.booking.startsAt, language)} —{' '}
                {dateLabel(appointment.booking.endsAt, language)}
              </p>
            ) : (
              appointment.checkedAt && <p>{copy.agendaRemoved}</p>
            )}
            {appointment.checkedAt && (
              <p className="small muted">
                {copy.agendaCheckedAt}{' '}
                {dateLabel(appointment.checkedAt, language)}
              </p>
            )}
            {appointment.synchronizationRequired && (
              <p className="notice-inline">
                {copy.agendaSynchronizationRequired}
              </p>
            )}
            <a
              className="button-link"
              href={`/?companyId=${scope.companyId}&botId=${scope.botId}&view=inbox&conversationId=${appointment.conversationId}`}
            >
              {copy.agendaOpenConversation}
            </a>
          </article>
        ))}
      </div>
      <div className="form-actions">
        {before && (
          <button disabled={agenda.loading} onClick={() => setBefore(null)}>
            {copy.firstPage}
          </button>
        )}
        {agenda.data?.nextCursor && (
          <button
            disabled={agenda.loading}
            onClick={() => setBefore(agenda.data!.nextCursor)}
          >
            {copy.agendaOlder}
          </button>
        )}
      </div>
    </section>
  );
}
